/**
 * ClusterDetailPanel Component
 *
 * Floating panel that shows details of a selected cluster.
 * Displays cluster items, stats, and actions.
 */

import { useMemo } from 'react';
import { X, Layers, Globe2, Flag, MapPin, Edit, Play, Clock } from 'lucide-react';
import { Card, Button, Badge, ScrollArea } from '@/components/ui';
import { cn } from '@/lib/utils';
import { calculateClusterStats, getClusterItemDisplayName, resolveClusterToLocations } from '@/services/clusterService';
import { useDateFormat } from '@/hooks/useDateFormat';
import type { Cluster, Region, Country, Location } from '@simcheck/shared-types';

export interface ClusterDetailPanelProps {
  clusterId: string;
  clusters: Cluster[];
  regions: Region[];
  countries: Country[];
  locations: Location[];
  onEdit: (cluster: Cluster) => void;
  onClose: () => void;
}

export function ClusterDetailPanel({
  clusterId,
  clusters,
  regions,
  countries,
  locations,
  onEdit,
  onClose,
}: ClusterDetailPanelProps) {
  const fmt = useDateFormat();

  // Find the cluster
  const cluster = clusters.find(c => c.id === clusterId);

  // Calculate stats
  const stats = useMemo(() => {
    if (!cluster) return { locationCount: 0, modemCount: 0 };
    return calculateClusterStats(cluster.items, regions, countries, locations);
  }, [cluster, regions, countries, locations]);

  // Get resolved locations for display
  const resolvedLocations = useMemo(() => {
    if (!cluster) return [];
    // Synchronously calculate resolved locations
    const locationIds = new Set<string>();
    for (const item of cluster.items) {
      switch (item.type) {
        case 'location':
          locationIds.add(item.id);
          break;
        case 'country':
          locations
            .filter(l => l.countryId === item.id && l.status === 'active')
            .forEach(l => locationIds.add(l.id));
          break;
        case 'region':
          locations
            .filter(l => l.regionId === item.id && l.status === 'active')
            .forEach(l => locationIds.add(l.id));
          break;
      }
    }
    return Array.from(locationIds).map(locationId => {
      const location = locations.find(l => l.id === locationId);
      const country = location ? countries.find(c => c.id === location.countryId) : null;
      const region = location ? regions.find(r => r.id === location.regionId) : null;
      return {
        locationId,
        locationName: location?.displayName || location?.name || 'Unknown',
        countryName: country?.name || 'Unknown',
        countryFlag: country?.flag || '',
        regionName: region?.name || 'Unknown',
        modemCount: location?.modemCount || 0,
      };
    });
  }, [cluster, regions, countries, locations]);

  if (!cluster) {
    return null;
  }

  const typeIcons = {
    region: Globe2,
    country: Flag,
    location: MapPin,
  };

  const typeColors = {
    region: 'text-accent-blue',
    country: 'text-accent-purple',
    location: 'text-primary',
  };

  return (
    <div className="fixed right-4 top-20 bottom-4 w-80 z-50">
      <Card className="h-full flex flex-col shadow-xl">
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-accent-purple" />
            <h3 className="font-semibold text-foreground">{cluster.name}</h3>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Content */}
        <ScrollArea className="flex-1">
          <div className="p-4 space-y-4">
            {/* Description */}
            {cluster.description && (
              <p className="text-sm text-muted-foreground">{cluster.description}</p>
            )}

            {/* Stats */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-muted/50">
                <div className="text-2xl font-mono font-bold text-foreground">
                  {stats.locationCount}
                </div>
                <div className="text-xs text-muted-foreground">Locations</div>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <div className="text-2xl font-mono font-bold text-foreground">
                  {stats.modemCount}
                </div>
                <div className="text-xs text-muted-foreground">Modems</div>
              </div>
            </div>

            {/* Tags */}
            {cluster.tags && cluster.tags.length > 0 && (
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Tags
                </label>
                <div className="flex flex-wrap gap-1">
                  {cluster.tags.map(tag => (
                    <Badge key={tag} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Cluster Items */}
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Included Items ({cluster.items.length})
              </label>
              <div className="space-y-1">
                {cluster.items.map(item => {
                  const Icon = typeIcons[item.type];
                  const colorClass = typeColors[item.type];
                  const displayName = getClusterItemDisplayName(
                    item,
                    regions,
                    countries,
                    locations
                  );

                  return (
                    <div
                      key={`${item.type}:${item.id}`}
                      className="flex items-center gap-2 p-2 rounded bg-muted/30"
                    >
                      <Icon className={cn('w-4 h-4', colorClass)} />
                      <span className="text-sm flex-1">{displayName}</span>
                      <Badge variant="secondary" className="text-xs capitalize">
                        {item.type}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Resolved Locations */}
            {resolvedLocations.length > 0 && (
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Resolved Locations ({resolvedLocations.length})
                </label>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {resolvedLocations.map(loc => (
                    <div
                      key={loc.locationId}
                      className="flex items-center gap-2 p-2 rounded bg-muted/20 text-sm"
                    >
                      <span>{loc.countryFlag}</span>
                      <span className="flex-1 truncate">{loc.locationName}</span>
                      <span className="text-xs text-muted-foreground">
                        {loc.modemCount} modem{loc.modemCount !== 1 ? 's' : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Created info */}
            <div className="pt-2 border-t border-border text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <Clock className="w-3 h-3" />
                <span>
                  Created {fmt.formatDate(cluster.createdAt)}
                </span>
              </div>
            </div>
          </div>
        </ScrollArea>

        {/* Actions */}
        <div className="p-4 border-t border-border flex gap-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => onEdit(cluster)}
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Button>
          <Button
            variant="default"
            className="flex-1"
            onClick={() => {
              // TODO: Navigate to test execution with this cluster pre-selected
              console.log('Run test with cluster:', cluster.id);
            }}
          >
            <Play className="w-4 h-4 mr-2" />
            Run Test
          </Button>
        </div>
      </Card>
    </div>
  );
}

export default ClusterDetailPanel;
